import { useEffect, useState } from 'react'

import SearchIcon from '@heroicons/react/solid/SearchIcon'
import { useHistory, useRouteMatch } from 'react-router'

import { SearchEntity, SearchEntityType } from '@tribeplatform/gql-client/types'
import {
  SystemSpaceSlug,
  useSearch,
  useSpaceUtil,
} from '@tribeplatform/react-sdk/hooks'
import { Keys } from '@tribeplatform/react-sdk/lib'
import { Autocomplete } from '@tribeplatform/react-ui-kit/Autocomplete'
import { SpinnerIcon } from '@tribeplatform/react-ui-kit/icons'
import { StackedList } from '@tribeplatform/react-ui-kit/StackedList'
import { useQuery } from '@tribeplatform/slate-kit/utils'

import { useDebounce } from '../../utils/useDebounce'
import { SearchRow, SearchRowPage } from './SearchRow'
import { getSearchItemLink } from './utils'

// Remove post results with no spaces
export const cleanupSearchResult = data => {
  if (!data) return null
  data.hits = data.hits.map(section => {
    section.hits = section.hits.filter(hit => {
      return !(section?.entityType === SearchEntityType.POST && !hit?.in)
    })
    return section
  })

  data.hits = data.hits.filter(section => section.hits.length)

  return data
}

const PAGE_RESULT = 'search-page-shortcut'

export const SearchBar = ({ autoFocus = false }) => {
  const { query, type } = useQuery()
  const { replace } = useHistory()
  const { generatePath } = useSpaceUtil()
  const [search, setSearch] = useState('')

  const history = useHistory()
  const debouncedQuery = useDebounce(search, 300)
  const match = useRouteMatch('/search')
  const enabled = match === null

  useEffect(() => {
    setSearch(query || '')
  }, [query, type])

  const { data, isLoading, isFetching } = useSearch({
    variables: {
      input: {
        query: debouncedQuery,
      },
    },
    fields: 'all',
    useQueryOptions: {
      queryKey: Keys.getSearchKey({
        input: { query: debouncedQuery },
      }),
      keepPreviousData: true,
      enabled,
    },
  })

  const onSelect = (value: SearchEntity) => {
    if (value) {
      if (value === PAGE_RESULT) {
        history.push(`/search?query=${search}${type ? `&type=${type}` : ``}`)
      } else {
        history.push(getSearchItemLink(value))
      }
    }
  }

  const onEnter = ({ highlightedIndex, inputValue }) => {
    if (inputValue && highlightedIndex < 1) {
      const queryParams: Record<string, string> = { query: search }
      if (type) queryParams.type = type
      history.push(generatePath(SystemSpaceSlug.SEARCH, null, queryParams))
    }
  }

  return (
    <div className="flex items-center flex-1">
      <Autocomplete
        itemToString={value => value?.title || ''}
        value={search}
        onChange={onSelect}
        options={[
          PAGE_RESULT,
          ...(data?.hits.flatMap(category => category.hits) || []),
        ]}
        onInputChange={query => {
          setSearch(query)
          if (query && !enabled) {
            const queryParams: Record<string, string> = { query }
            if (type) queryParams.type = type
            replace(generatePath(SystemSpaceSlug.SEARCH, null, queryParams))
          }
        }}
        className="w-full relative"
        loading={isLoading}
        defaultHighlightedIndex={enabled ? 0 : undefined}
        clearInputOnSelect={enabled}
        clearInputOnBlur={enabled}
      >
        <Autocomplete.Input
          autoFocus={autoFocus}
          leadingIcon={
            enabled && isFetching ? (
              <SpinnerIcon className="animate-spin" />
            ) : (
              <SearchIcon />
            )
          }
          placeholder="Search..."
          onEnter={onEnter}
          value={search}
          showClearButton={false}
          // type="search"
        />
        {enabled && (
          <Autocomplete.Items className="h-full overflow-y-auto isolate">
            <Autocomplete.Item index={0} value="search-page-shortcut-value">
              <SearchRowPage
                search={search}
                link={`/search?query=${query}${type ? `&type=${type}` : ``}`}
                spacing="sm"
              />
            </Autocomplete.Item>
            {data?.totalCount > 0 &&
              cleanupSearchResult(data)?.hits.reduce(
                (result, category, sectionIndex) => {
                  result.sections.push(
                    // eslint-disable-next-line react/no-array-index-key
                    <StackedList.Group key={sectionIndex}>
                      <StackedList.GroupTitle className="capitalize">
                        {category.entityType}
                      </StackedList.GroupTitle>
                      {category.hits.map((hit, personIndex) => {
                        const index = result.itemIndex
                        result.itemIndex += 1
                        return (
                          <Autocomplete.Item
                            // eslint-disable-next-line react/no-array-index-key
                            key={personIndex}
                            value={hit}
                            index={index}
                          >
                            <SearchRow
                              spacing="sm"
                              category={category}
                              row={hit}
                            />
                          </Autocomplete.Item>
                        )
                      })}
                    </StackedList.Group>,
                  )
                  return result
                },
                { sections: [], itemIndex: 1 },
              ).sections}
            {isLoading && (
              <Autocomplete.ItemsEmpty>Searching...</Autocomplete.ItemsEmpty>
            )}
          </Autocomplete.Items>
        )}
      </Autocomplete>
    </div>
  )
}
